import { useState } from "react";
import { MapPin, Video } from "lucide-react";

const streetUrl = (lat, lng, w = 320, h = 120, z = 17) =>
  `https://maps.wikimedia.org/img/osm-intl,${z},${lat},${lng},${w}x${h}@2x.png`;

/**
 * Street-level snapshot thumbnail of a property's location.
 * Uses free Wikimedia static map tiles — shows roads, nearby amenities, satellite-adjacent context.
 * variant: 'strip' (card bottom), 'tile' (square), 'inline' (compact badge)
 */
export const StreetSnapshot = ({ lat, lng, virtualTourUrl, variant = "strip" }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!lat || !lng) return null;
  const url = streetUrl(lat, lng, variant === "tile" ? 400 : 320, variant === "tile" ? 400 : 120);

  if (variant === "strip") {
    return (
      <div className="relative h-16 w-full bg-[var(--surface)] overflow-hidden" data-testid="street-snapshot-strip">
        {!failed && (
          <img
            src={url}
            alt="Street-level map"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? "opacity-80" : "opacity-0"}`}
          />
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(253,252,247,0.9), rgba(253,252,247,0) 30%, rgba(253,252,247,0) 70%, rgba(253,252,247,0.9))" }} />
        <div className="absolute inset-0 flex items-center justify-between px-3 text-[11px] font-semibold text-[var(--teak)]">
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-[var(--primary)]" /> Street view
          </span>
          {virtualTourUrl && (
            <span className="flex items-center gap-1 text-[var(--emerald)]">
              <Video className="w-3.5 h-3.5" /> 360° tour
            </span>
          )}
        </div>
      </div>
    );
  }

  if (variant === "tile") {
    return (
      <div className="relative aspect-square w-full rounded-xl overflow-hidden border border-[var(--line)]" data-testid="street-snapshot-tile">
        {!failed ? (
          <img
            src={url}
            alt="Street-level map"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--ink-muted)] text-xs">Map unavailable</div>
        )}
        <div className="absolute top-2 left-2 chip chip-gold text-[10px]">
          <MapPin className="w-3 h-3" /> Street
        </div>
      </div>
    );
  }

  return null;
};
